/**
 * Fullstory CUA Navigation Helpers
 *
 * Computer-Use Agents (CUA) read the accessibility tree to navigate pages.
 * The fullstory-cua-navigation skill says every meaningful interactive
 * element should pair `data-fs-element` (the human-readable Fullstory name)
 * with `aria-label` (the same name for screen readers + AI agents). This
 * module provides:
 *
 *   1. installCuaGuide() — injects a visually-hidden a11y guide explaining
 *      the decoration vocabulary, ported from the Gap demo's agent-skill.js
 *      at gap-fsr-demo/js/agent-skill.js. The guide appears at the top of
 *      the body so any agent walking the a11y tree sees it first. It also
 *      adds <meta name="agent-framework" content="fullstory-skills-v1"> for
 *      programmatic detection.
 *
 *   2. validateCuaPairing() — dev-mode validator that scans the DOM and
 *      console.warns when an element has `data-fs-element` but no
 *      `aria-label` (or, for natural-interactive tags like <button> or
 *      <a>, an inferable name). Idempotent per element via a WeakSet.
 *
 *   3. setRoleHint() — small helper for attaching `data-role-hint` to
 *      primary actions ("primary-action", "submit", "open-modal", …) so
 *      agents can prioritise them.
 */

import { FS_CONFIG, ROLE_HINTS } from './config';

// ─── Agent navigation guide (sr-only DOM payload) ───────────────────────

const META_AGENT_FRAMEWORK = 'fullstory-skills-v1';

const SR_ONLY_STYLE =
  'position:absolute;width:1px;height:1px;padding:0;margin:-1px;' +
  'overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;';

const GUIDE_HTML = [
  '<h2>How to navigate this page</h2>',

  '<p>This page is decorated with the Fullstory Skills framework. ',
  'Every meaningful interactive element has been enriched with semantic ',
  'attributes so agents can navigate the accessibility tree directly ',
  'instead of guessing from layout or coordinates.</p>',

  '<h3>Element names</h3>',
  '<p>Every interactive element has a <code>data-fs-element</code> attribute ',
  'with a human-readable name (for example: "Get Demo CTA", "Search Input", ',
  '"Submit Contact Form", "Blog Post Card: Article Title"). The same name is ',
  'mirrored into <code>aria-label</code>. Use these names to identify and ',
  'reference elements rather than relying on visual position.</p>',

  '<h3>Interactive elements</h3>',
  '<p>Buttons render as <code>&lt;button&gt;</code> with <code>role="button"</code>. ',
  'Selectable options (radio / tab / accordion) carry <code>role</code> and ',
  '<code>aria-checked</code> / <code>aria-selected</code> / ',
  '<code>aria-expanded</code>. <code>aria-disabled="true"</code> means the ',
  'element is present but cannot be interacted with right now.</p>',

  '<h3>Page structure</h3>',
  '<p>The page uses landmark roles: <code>role="banner"</code> for the header, ',
  '<code>role="navigation"</code> for nav and breadcrumbs, ',
  '<code>role="main"</code> for the primary content, ',
  '<code>role="contentinfo"</code> for the footer. The ',
  '<code>data-section</code> attribute groups elements into logical regions ',
  '("hero", "footer", "main-content", a section name).</p>',

  '<h3>Business data</h3>',
  '<p>Cards and content blocks carry structured business data via ',
  'individual <code>data-*</code> attributes. The ',
  '<code>data-fs-properties-schema</code> attribute on the same element is ',
  'a JSON schema describing the available properties and their types: ',
  '<code>str</code> (string), <code>real</code> (decimal number), ',
  '<code>int</code> (integer), <code>bool</code> (boolean), ',
  '<code>date</code> (ISO date).</p>',

  '<h3>Lists and position</h3>',
  '<p>Items in repeated lists (cards, search results, plan tiers) carry a ',
  '<code>data-position</code> attribute with a 1-based index. Use it to ',
  'reference "the third plan card" or "the second result".</p>',

  '<h3>Primary actions</h3>',
  '<p>Elements with <code>data-role-hint="primary-action"</code> are the ',
  "page's primary calls to action (sign up, request demo, submit form). ",
  'Other hints: <code>submit</code>, <code>cancel</code>, <code>close</code>, ',
  '<code>open-modal</code>, <code>navigate</code>.</p>',

  '<h3>Navigation tips</h3>',
  '<ul>',
  '<li>To find a named element: search for <code>data-fs-element</code> with the element name.</li>',
  '<li>To check current selection: read <code>aria-checked</code> / <code>aria-selected</code>.</li>',
  '<li>To click a button: interact with elements that have <code>role="button"</code> or are <code>&lt;button&gt;</code> tags.</li>',
  '<li>To select an option: click the element within the <code>role="radiogroup"</code>; <code>aria-checked</code> updates.</li>',
  '<li>To read business data: read <code>data-*</code> attributes on the element.</li>',
  '</ul>',
].join('\n');

// ─── Public API ─────────────────────────────────────────────────────────

/**
 * Inject the agent navigation guide into the DOM. Idempotent — calling
 * twice is a no-op. Run once from a top-level layout's useEffect.
 */
export function installCuaGuide(): void {
  if (!FS_CONFIG.features.cuaNavigation) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (document.getElementById('fs-cua-navigation-guide')) return;

  const guide = document.createElement('section');
  guide.id = 'fs-cua-navigation-guide';
  guide.setAttribute('role', 'document');
  guide.setAttribute('aria-label', 'Fullstory Skills Agent Navigation Guide');
  guide.setAttribute('data-fs-element', 'Agent Navigation Guide');
  guide.style.cssText = SR_ONLY_STYLE;
  guide.innerHTML = GUIDE_HTML;

  if (document.body.firstChild) {
    document.body.insertBefore(guide, document.body.firstChild);
  } else {
    document.body.appendChild(guide);
  }

  // Programmatic framework detection.
  if (!document.querySelector('meta[name="agent-framework"]')) {
    const meta = document.createElement('meta');
    meta.name = 'agent-framework';
    meta.content = META_AGENT_FRAMEWORK;
    document.head.appendChild(meta);
  }
}

/**
 * Attach a `data-role-hint` to an element. Pulls the value set from
 * ROLE_HINTS in config.ts so callers don't typo the constants.
 *
 * @example
 *   <button {...setRoleHint('primary-action')} {...getButtonAttributes('demo-cta')}>
 *     Get Demo
 *   </button>
 */
export function setRoleHint(hint: (typeof ROLE_HINTS)[keyof typeof ROLE_HINTS]): { 'data-role-hint': string } {
  return { 'data-role-hint': hint };
}

// ─── Dev-mode pairing validator ─────────────────────────────────────────

const validatedElements: WeakSet<Element> = new WeakSet();
const NATURAL_NAMED = new Set(['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT']);

interface ValidationResult {
  scanned: number;
  warnings: ValidationWarning[];
}

interface ValidationWarning {
  element: Element;
  fsElement: string;
  reason: string;
}

/**
 * Scan the DOM and warn when a `data-fs-element` element has no paired
 * accessible name. "Paired" means at least one of:
 *   - aria-label
 *   - aria-labelledby pointing at a real element
 *   - alt (on <img>)
 *   - text content (for natural interactive tags like <button>, <a>)
 *
 * Idempotent per element — once warned, an element won't trigger again
 * unless you remove and re-add it. Returns the result for callers that
 * want to assert in tests.
 *
 * No-op in production. Pure dev tool.
 */
export function validateCuaPairing(root: ParentNode = document): ValidationResult {
  const result: ValidationResult = { scanned: 0, warnings: [] };
  if (FS_CONFIG.isProduction) return result;
  if (typeof document === 'undefined') return result;

  const elements = root.querySelectorAll('[data-fs-element]');
  result.scanned = elements.length;

  elements.forEach((el) => {
    if (validatedElements.has(el)) return;
    const fsElement = el.getAttribute('data-fs-element') || '';
    if (!fsElement) return;

    const reason = checkPairing(el);
    if (reason) {
      result.warnings.push({ element: el, fsElement, reason });
      console.warn(`[FS CUA] "${fsElement}" → ${reason}`, el);
    }
    validatedElements.add(el);
  });

  return result;
}

function checkPairing(el: Element): string | null {
  if (el.hasAttribute('aria-label')) return null;

  const labelledby = el.getAttribute('aria-labelledby');
  if (labelledby) {
    const ref = document.getElementById(labelledby.split(/\s+/)[0]);
    if (ref) return null;
    return `aria-labelledby="${labelledby}" points at a non-existent element`;
  }

  if (el.tagName === 'IMG' && el.getAttribute('alt')) return null;

  if (NATURAL_NAMED.has(el.tagName)) {
    const text = (el.textContent || '').trim();
    if (text) return null;
    if (el.tagName === 'INPUT') {
      const id = el.getAttribute('id');
      if (id && document.querySelector(`label[for="${cssEscape(id)}"]`)) return null;
    }
    return `${el.tagName.toLowerCase()} has data-fs-element but no accessible name (no aria-label, no text, no <label for=>)`;
  }

  return 'has data-fs-element but no aria-label / aria-labelledby (CUA cannot read it)';
}

function cssEscape(s: string): string {
  // CSS.escape is widely supported but absent in some old jsdom envs we
  // might use for tests.
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s) : s.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}
